import { FlightSkeletonLine } from '@lt/components/icons-react/userflow/flights/skeleton';
import { i18n } from '@lt/localization';
import React, { useEffect, useState } from 'react';
import { formatCurrency } from 'src/utils/stringUtils';
import styled, { css } from 'styled-components';
import {
  LUGGAGE_FILTER_PANEL_CONTAINER_DATA_TEST_ID,
  LUGGAGE_FILTER_PANEL_ELEMENT_DATA_TEST_ID,
} from './constants';
import type { LuggageFilterPanelProps, LuggageFilters } from './types';

const filterNames = {
  all: i18n.t('checkout.filter.luggage.all'),
  heavy: i18n.t('checkout.filter.luggage.heavy'),
  hold: i18n.t('checkout.filter.luggage.hold'),
};

const fromText = i18n.t('commonPages.from');
const noFlightsText = i18n.t('checkout.filter.empty');

/**
 * Панелька над перелетами для фильтрации по багажу
 */
export const LuggageFilterPanel = ({
  filterData,
  applyFilters,
  className,
  isMobile,
  getMinPrice,
}: LuggageFilterPanelProps): JSX.Element => {
  const [filters, setFilters] = useState<LuggageFilters>([]);

  useEffect(() => {
    if (filterData.length)
      setFilters(() => {
        const filtersFromStore = filterData.map(
          ({ selected, disabled, luggage_type, min_fuel_charge_by_tours }) => ({
            isSelected: selected,
            isDisabled: disabled,
            title: filterNames[luggage_type],
            description: !disabled
              ? `${fromText} ${formatCurrency(
                  getMinPrice({ min_fuel_charge_by_tours }) || '',
                )}`
              : noFlightsText,
            type: luggage_type,
          }),
        );
        /**
         * В массиве с бека приходят сначала hold, all, heavy.
         *  Но на фронте нужно отрисовывать в другом порядке:
         * "Все", "С багажом", "С багажом от 20кг"
         */
        return [filtersFromStore[1], filtersFromStore[0], filtersFromStore[2]];
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterData]);

  useEffect(() => {
    /**
     * По умолчанию опция 'all' должна быть выбрана,
     * но с бека в ручке flights все опции приходят невыбранными
     */
    if (filters.every(({ isSelected }) => !isSelected) && filters.length) {
      setFilters((currentFilters) => [
        { ...currentFilters[0], isSelected: true },
        currentFilters[1],
        currentFilters[2],
      ]);
    }
  }, [filters]);

  const onChangeFilter = (type: string) => {
    const diff = filterData.find(({ luggage_type }) => luggage_type === type);
    if (diff)
      applyFilters({
        path: 'luggage_types',
        diff: { ...diff, selected: true },
      });
  };
  const checkChangeFilter = (
    type: string,
    isDisabled?: boolean,
    isSelect?: boolean,
  ) => {
    // не имеет смысла вызывать action, если фильтр уже выбран или в нем отсутствуют варианты
    if (!isDisabled && !isSelect) {
      onChangeFilter(type);
    }
  };

  if (!filters.length)
    return (
      <Container>
        <SkeletonItem>
          <StyledSkeletonLine width={35} height={1} />
          <StyledSkeletonLine width={51} height={1} />
        </SkeletonItem>
        <SkeletonItem>
          <StyledSkeletonLine width={35} height={1} />
          <StyledSkeletonLine width={51} height={1} />
        </SkeletonItem>
        <SkeletonItem>
          <StyledSkeletonLine width={35} height={1} />
          <StyledSkeletonLine width={51} height={1} />
        </SkeletonItem>
      </Container>
    );

  return (
    <StyledPositionContainer>
      <Container
        className={className}
        data-testid={LUGGAGE_FILTER_PANEL_CONTAINER_DATA_TEST_ID}
      >
        {filters.map(
          ({
            isSelected,
            isDisabled,
            title,
            description,
            type,
          }): JSX.Element => (
            <Item
              $isSelect={isSelected}
              $isDisabled={isDisabled}
              key={type}
              onClick={() => checkChangeFilter(type, isDisabled, isSelected)}
              data-testid={`${LUGGAGE_FILTER_PANEL_ELEMENT_DATA_TEST_ID}-${type}`}
            >
              <Title>{title}</Title>
              <Description>{description}</Description>
            </Item>
          ),
        )}
        {isMobile && <StyledFade />}
      </Container>
    </StyledPositionContainer>
  );
};

const SkeletonItem = styled.div`
  display: flex;
  padding: 18px 12px 16px 12px;
  flex-direction: column;
  justify-content: space-between;
  flex: 1;
`;

const StyledFade = styled.div`
  width: 40px;
  height: 100%;

  position: absolute;
  right: 0px;
  top: 0;

  background: linear-gradient(90deg, rgba(255, 255, 255, 0) 0%, #fff 100%);
`;

const StyledSkeletonLine = styled(FlightSkeletonLine)`
  & > svg {
    stroke: #e6e6e6;
  }
`;

const StyledPositionContainer = styled.div`
  position: relative;
`;

const Container = styled.ul`
  width: 100%;
  height: 66px;
  padding: 8px;
  margin-bottom: 16px;

  overflow-x: scroll;
  // Отключить скроллбар во всех браузерах
  &::-webkit-scrollbar {
    display: none;
  }
  -ms-overflow-style: none;
  scrollbar-width: none;

  display: flex;
  justify-content: space-between;

  border-radius: 8px;
  background: #fff;
  box-shadow: 0px 2px 4px 0px rgba(210, 208, 197, 0.8);
`;

const Item = styled.li<{
  $isSelect?: boolean;
  $isDisabled?: boolean;
}>`
  flex: 1;
  height: 100%;

  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: start;

  padding: 8px 12px;
  border-radius: 4px;

  font-size: 14px;
  line-height: 16px;
  white-space: pre;

  cursor: pointer;
  color: #4d4d4d;

  & + & {
    margin-left: 8px;
  }

  ${({ $isSelect }) =>
    $isSelect &&
    css`
      border: 1px solid #f8e71c;
      background: #fffbe5;
      color: #17181a;
      cursor: default;
    `}

  ${({ $isDisabled }) =>
    $isDisabled &&
    css`
      color: #919499;
      cursor: default;
    `}
`;

const Title = styled.p`
  font-weight: 700;
`;

const Description = styled.span`
  font-weight: 400;
`;
